import { Alert, Button, Card, Space } from "antd";
import type { Claim, DeviceVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CLAIM_STATE } from "@/meta/logistics/deviceClaim";
import { useDeviceClaimActionAccess } from "./useDeviceClaimActionAccess";

export interface DeviceClaimDetailPageProps {
    claimId?: number | string;
    loadClaim?: (id: number | string) => Promise<Partial<Claim> | undefined>;
    loadDevice?: (id: number | string) => Promise<Partial<DeviceVo> | undefined>;
    permissions?: string[];
    banButton?: boolean;
}

/** 复用 Vue 设备认领详情接口。 */
export async function loadDeviceClaim(id: number | string): Promise<Partial<Claim> | undefined> {
    const response = await request.service.get<Partial<Claim>>(`/claim/${id}`);
    return response.data;
}

/** 复用 Vue 设备详情接口。 */
export async function loadClaimDevice(id: number | string): Promise<Partial<DeviceVo> | undefined> {
    const response = await request.service.get<Partial<DeviceVo>>(`/device/vo/${id}`);
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatCustomer = (customerInfo?: Claim["customerInfo"]) =>
    customerInfo
        ? `${customerInfo.institutionName || customerInfo.realName || DEFAULT_VALUE} | ${customerInfo.mobile || DEFAULT_VALUE} | ${customerInfo.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

/** 物流管理-设备认领详情，覆盖 Vue 详情字段、状态分支和设备信息。 */
export function DeviceClaimDetailPage({
    claimId,
    loadClaim = loadDeviceClaim,
    loadDevice = loadClaimDevice,
    permissions,
    banButton
}: DeviceClaimDetailPageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { canBind, isButtonBanned } = useDeviceClaimActionAccess({ permissions, banButton });
    const resolvedId = claimId ?? params.id ?? 0;
    const [claim, setClaim] = useState<Partial<Claim>>();
    const [device, setDevice] = useState<Partial<DeviceVo>>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [deviceErrorMessage, setDeviceErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadClaim(resolvedId)
            .then(async result => {
                if (disposed) return;
                setClaim(result);
                if (result?.deviceId) {
                    try {
                        const deviceResult = await loadDevice(result.deviceId);
                        if (!disposed) setDevice(deviceResult);
                    } catch {
                        // 关联设备失败不应覆盖已经成功加载的认领详情。
                        if (!disposed) setDeviceErrorMessage("设备信息加载失败");
                    }
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("设备认领详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadClaim, loadDevice, resolvedId]);

    // Vue 详情只在已完成状态展示处理摘要，只在待处理状态展示处理入口。
    const isClaimed = Number(claim?.state) === CLAIM_STATE.CLAIMED.code;
    const isClaiming = Number(claim?.state) === CLAIM_STATE.CLAIMING.code;

    return (
        <section aria-label="device-claim-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[
                        { label: "编号", value: claim?.id ?? DEFAULT_VALUE },
                        ...(isClaimed ? [{ label: "处理人", value: claim?.handlerName || DEFAULT_VALUE }] : [])
                    ]}
                    rightInfos={[
                        { label: "创建时间", value: formatDate(claim?.createdTime) },
                        ...(isClaimed ? [{ label: "处理时间", value: formatDate(claim?.handleTime) }] : [])
                    ]}
                    stateDesc={CLAIM_STATE.getIntroByCode(claim?.state) || DEFAULT_VALUE}
                    title="设备认领详情"
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {claim ? (
                    <>
                        <div className="mb-4 mt-6 text-base font-medium">申领信息</div>
                        <ZhGrid cols={4} gap={[30, 0]}>
                            <ZhBaseItem label="承运商账号" value={formatCustomer(claim.customerInfo)} />
                            <ZhBaseItem label="船舶">
                                {claim.shipId ? (
                                    <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${claim.shipId}`}>
                                        {claim.shipNameCn
                                            ? `${claim.shipNameCn} / ${claim.shipName || DEFAULT_VALUE}`
                                            : claim.shipName || DEFAULT_VALUE}
                                    </Link>
                                ) : (
                                    claim.shipName || DEFAULT_VALUE
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="MMSI" value={claim.shipId || DEFAULT_VALUE} />
                        </ZhGrid>
                    </>
                ) : null}
                {claim?.deviceId ? (
                    <>
                        <div className="mb-4 mt-6 text-base font-medium">设备信息</div>
                        {deviceErrorMessage ? <Alert message={deviceErrorMessage} role="alert" type="error" /> : null}
                        <ZhGrid cols={4} gap={[30, 0]}>
                            <ZhBaseItem label="设备名称" value={device?.device?.name || DEFAULT_VALUE} />
                            <ZhBaseItem label="序列号" value={device?.device?.serialNo || DEFAULT_VALUE} />
                            <ZhBaseItem label="安装位置" value={device?.device?.position || DEFAULT_VALUE} />
                            <ZhBaseItem label="安装人" value={claim.installer?.name || DEFAULT_VALUE} />
                        </ZhGrid>
                    </>
                ) : null}
                <Space className="mt-6">
                    {isClaiming && canBind ? (
                        <Link to={`/logistics/deviceClaim/form/${claim?.id ?? resolvedId}`}>
                            <Button aria-label="处理" disabled={isButtonBanned} type="link">
                                处理
                            </Button>
                        </Link>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </Card>
        </section>
    );
}
